import React, { useEffect, useRef, useState } from 'react';
import './SurveyLibraryChangeRequest.scss';
import { array, bool, func, number } from 'prop-types';
import ActionableHeaderCard from 'ps-components/Card/ActionableHeaderCard';
import AdvancedRow from 'ps-components/AdvancedTable/AdvancedRow';
import AdvancedCell from 'ps-components/AdvancedTable/AdvancedCell';
import { shortDateString } from 'ps-components/lib/formatters';
import Pagination from 'ps-components/Pagination/Pagination';
import Table from 'ps-components/Table/Table/Table';
import TableBody from 'ps-components/Table/TableBody/TableBody';
import { DATA_SET_TYPE, SLC_AUDIT_TRAIL_LIMIT } from '../SurveyLibraryChangeActions';
import ModalHeader from 'ps-components/Modal/ModalHeader/ModalHeader';
import ModalBody from 'ps-components/Modal/ModalBody/ModalBody';
import ModalFooter from 'ps-components/Modal/ModalFooter/ModalFooter';
import Modal from 'ps-components/Modal/Modal';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import { SLIDEOUTS } from '../../Slideout/Slideouts';
import {
  SUBMIT_STATUS_FAILURE,
  SUBMIT_STATUS_IN_PROGRESS,
  SUBMIT_STATUS_SUCCESS
} from '../Agreement/SLUAgreementReducer';

function SurveyLibraryChangeRequest(props) {
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [notesBeingEditedForRequestId, setNotesBeingEditedForRequestId] = React.useState(null);
  const [modifiedNotes, setModifiedNotes] = useState('');
  const notesInputRef = useRef(null);

  //temporary, will later persist changes to backend
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);

  useEffect(() => {
    notesInputRef.current?.focus();
  }, [notesInputRef.current]);

  useEffect(() => {
    props.getAuditTrail();
  }, []);

  const pageAuditTrail = (limit, offset) => {
    props.setOffset(offset);
    props.getAuditTrail(limit, offset);
  };

  const renderPendingRequests = () => {
    return props.pendingSurveyRequests.map(request => {
      return (
        <AdvancedRow>
          <AdvancedCell className="survey__cell" width="35%">
            <div className="survey-code">{request.surveyCode}</div>
            <div className="survey-name">{request.surveyName}</div>
            {notesBeingEditedForRequestId === request.requestId ? (
              <React.Fragment>
                <div>
                  <textarea
                    className="notes__textarea"
                    defaultValue={request.notes}
                    onChange={handleNotesOnChange}
                    onKeyDown={handleNotesKeyUp.bind(this, request)}
                    onBlur={handleNotesOnBlur.bind(this, request)}
                    ref={notesInputRef}
                    id={request.requestId}
                  />
                </div>
                <div className="survey-comment__instruction-text">
                  <span className="bold">Enter</span> save
                  <span className="bold space-left">Esc</span> cancel
                </div>
              </React.Fragment>
            ) : (
              <a className="add-note__link" onClick={onAddNoteClick.bind(this, request)}>
                {request?.notes?.trim() ? request.notes : '+ Add a note'}
              </a>
            )}
          </AdvancedCell>
          <AdvancedCell width="15%">
            {!request.surveyCode && !!request.surveyName ? 'Add New Survey' : 'Add from MarketPay Library'}
          </AdvancedCell>
          <AdvancedCell className="status__cell" width="20%">
            <div className="status">Not Submitted</div>
            <div className="last-updated">{`Last Update ${shortDateString(new Date(request.lastModifiedDate))}`}</div>
          </AdvancedCell>
          <AdvancedCell width="10%">
            {request.surveyCode && (
              <Dropdown
                className="survey-request__dropdown"
                dropdownItems={['Standard', 'Custom']}
                text={request.dataSetType === DATA_SET_TYPE.CUSTOM ? 'Custom' : 'Standard'}
                itemOnClick={updateDataSetType.bind(this, request)}
                leftBound={true}
              />
            )}
          </AdvancedCell>
          {props.hasSubGroups && (
            <AdvancedCell width="15%">
              <Dropdown
                className="survey-request__dropdown"
                dropdownItems={['Site Only', 'Site and All Groups']}
                text={request.applyToSubgroups ? 'Site and All Groups' : 'Site Only'}
                itemOnClick={updateApplyToSubGroups.bind(this, request)}
                leftBound={true}
              />
            </AdvancedCell>
          )}
          <AdvancedCell width="5%" className="delete-column">
            <i className="icon-trash" onClick={showDeleteRequestModal.bind(this, request)} />
          </AdvancedCell>
        </AdvancedRow>
      );
    });
  };

  const renderAuditTrailRows = () => {
    if (props.auditTrails?.length === 0) {
      return (
        <AdvancedRow>
          <AdvancedCell className="no-requests__cell">No requests have been submitted</AdvancedCell>
        </AdvancedRow>
      );
    }
    return props.auditTrails.map(auditTrail => {
      return (
        <AdvancedRow>
          <AdvancedCell className="survey__cell" width="35%">
            <div className="survey-code">{auditTrail.surveyCode}</div>
            <div className="survey-name">{auditTrail.surveyName}</div>
            <div className="survey-notes">{auditTrail.notes}</div>
          </AdvancedCell>
          <AdvancedCell width="15%">
            {auditTrail.isNewSurvey ? 'Add New Survey' : 'Add from MarketPay Library'}
          </AdvancedCell>
          <AdvancedCell className="status__cell" width="20%">
            <div className="status">
              {auditTrail.status === 'vendor_verification' && 'Waiting on vendor’s verfication'}
              {auditTrail.status === 'completed' && (
                <React.Fragment>
                  <i className="icon-check" /> Completed
                </React.Fragment>
              )}
              {auditTrail.status === 'will_be_in_touch' && 'Customer service will be in touch'}
              {auditTrail.status === 'cs_assistance' && 'Requires customer service assistance'}
            </div>
            <div className="last-updated">{`Last Update ${shortDateString(getLastModifiedDate(auditTrail))}`}</div>
          </AdvancedCell>
          <AdvancedCell width="10%">
            {auditTrail.dataSetType === DATA_SET_TYPE.CUSTOM && 'Custom'}
            {auditTrail.dataSetType === DATA_SET_TYPE.STANDARD && 'Standard'}
          </AdvancedCell>
          {props.hasSubGroups && (
            <AdvancedCell width="15%">
              {auditTrail.applyToSubgroups === true && 'Site and All Groups'}
              {auditTrail.applyToSubgroups === false && 'Site Only'}
              {auditTrail.applyToSubgroups === null && 'Not Defined'}
            </AdvancedCell>
          )}
          <AdvancedCell width="5%" />
        </AdvancedRow>
      );
    });
  };

  const getLastModifiedDate = auditTrail => {
    return new Date(auditTrail.isPending ? auditTrail.beforeValidationDate : auditTrail.afterValidationDate);
  };

  const closeDeleteSurveyRequestModal = () => {
    setShouldShowDeleteModal(false);
  };

  const renderDeleteRequestModal = () => {
    return (
      <Modal
        className="delete-request__modal"
        allowClose={true}
        show={shouldShowDeleteModal}
        onClose={closeDeleteSurveyRequestModal}
      >
        <ModalHeader>
          <h3>Delete Survey Library Change Request</h3>
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete &quot;
          <span className="survey__label">
            {`${requestToDelete.surveyCode ? `${requestToDelete.surveyCode} - ` : ''}${requestToDelete.surveyName}`}
          </span>
          &quot;?
          {props.hasDeleteSurveyRequestError && (
            <div className="delete__error">Error occurred while deleting the survey request, please try again.</div>
          )}
        </ModalBody>
        <ModalFooter
          cancelBtnClick={closeDeleteSurveyRequestModal}
          submitBtnClick={deleteSurveyRequest}
          saveButtonDisabled={props.isDeletingSurveyRequest}
          submitBtnTxtName={
            props.isDeletingSurveyRequest ? <i className="icon-animate-spin icon-spin" /> : 'Delete Request'
          }
        />
      </Modal>
    );
  };

  const showDeleteRequestModal = request => {
    setRequestToDelete(request);
    setShouldShowDeleteModal(true);
    props.resetDeleteSurveyError();
  };

  const deleteSurveyRequest = () => {
    props.deleteSurveyRequest(requestToDelete).then(() => {
      closeDeleteSurveyRequestModal();
    });
  };

  const handleNotesOnChange = event => {
    setModifiedNotes(event.target.value);
  };

  function handleNotesKeyUp(survey, event) {
    if (event.key === 'Escape') {
      clearEditNotes();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const notes = event.target.value;
      if (notes !== survey.notes) {
        saveNotes(survey, notes);
      }
      clearEditNotes();
    }
  }

  const clearEditNotes = () => {
    setModifiedNotes('');
    setNotesBeingEditedForRequestId(null);
  };

  function handleNotesOnBlur(survey, event) {
    const notes = event.target.value;
    saveNotes(survey, notes);
    clearEditNotes();
  }

  const saveNotes = (survey, notes) => {
    survey.notes = notes;
    props.updatePendingSurvey(survey);
    props.updateSurvey(survey.requestId, notes);
    clearEditNotes();
  };

  const subscribeSurveys = (emailEnabled, userSignature) => {
    props.setSubmitStatus(SUBMIT_STATUS_IN_PROGRESS);
    props
      .subscribeSurveys(userSignature, emailEnabled)
      .then(() => {
        props.setSubmitStatus(SUBMIT_STATUS_SUCCESS);
      })
      .catch(() => {
        props.setSubmitStatus(SUBMIT_STATUS_FAILURE);
      });
  };

  function showAgreement() {
    props.initializeAgreement(true);
    props.showSlideout(SLIDEOUTS.SLU_AGREEMENT_SLIDEOUT, {
      hideSlideout: props.hideSlideout,
      submitAgreement: subscribeSurveys
    });
  }

  function updateDataSetType(survey, dataSetTypeOption) {
    let dataSetType;
    if (dataSetTypeOption === 'Custom') {
      dataSetType = DATA_SET_TYPE.CUSTOM;
    } else if (dataSetTypeOption === 'Standard') {
      dataSetType = DATA_SET_TYPE.STANDARD;
    }
    if (dataSetType && survey.dataSetType !== dataSetType) {
      survey.dataSetType = dataSetType;
      props.updatePendingSurvey(survey);
      props.updateSurvey(survey.requestId, undefined, dataSetType);
    }
  }

  function updateApplyToSubGroups(survey, applyToSubgroupsOption) {
    const applyToSubgroups = applyToSubgroupsOption === 'Site and All Groups';
    if (applyToSubgroups !== survey.applyToSubgroups) {
      survey.applyToSubgroups = applyToSubgroups;
      props.updatePendingSurvey(survey);
      props.updateSurvey(survey.requestId, undefined, undefined, survey.applyToSubgroups);
    }
  }

  function onAddNoteClick(survey) {
    setNotesBeingEditedForRequestId(survey.requestId);
  }

  return (
    <div className="slc-request__container">
      <div className="slc-header__row">
        <div className="header__label">Survey Library Change Request</div>
        {props.pendingSurveyRequests?.length > 0 && (
          <a className="pxl-btn pxl-btn--primary submit-change-request__btn" onClick={showAgreement}>
            {`Submit Change Request (${props.pendingSurveyRequests.length})`}
          </a>
        )}
      </div>
      <div className="slc-body__row">
        <div className="audit-table__header">
          <Table>
            <TableBody>
              <AdvancedRow>
                <AdvancedCell width="35%">Survey Name</AdvancedCell>
                <AdvancedCell width="15%">Change Requested</AdvancedCell>
                <AdvancedCell width="20%">Status</AdvancedCell>
                <AdvancedCell width="10%">Data Set Type</AdvancedCell>
                {props.hasSubGroups && <AdvancedCell width="15%">Assign Survey to</AdvancedCell>}
                <AdvancedCell width="5%" />
              </AdvancedRow>
            </TableBody>
          </Table>
        </div>
        {props.pendingSurveyRequests?.length > 0 && (
          <ActionableHeaderCard
            cardTitle={
              <span>
                <i className="icon-list-alt" />
                New Request
              </span>
            }
            isCollapsible={true}
            className="request-grouping submitted-requests__container"
          >
            <Table>
              <TableBody>{renderPendingRequests()}</TableBody>
            </Table>
          </ActionableHeaderCard>
        )}
        <ActionableHeaderCard
          cardTitle={
            <span>
              <i className="icon-clock" />
              Submitted Request
            </span>
          }
          isCollapsible={props.auditTrails?.length > 0}
          className="request-grouping submitted-requests__container"
        >
          <Table>
            <TableBody>{renderAuditTrailRows()}</TableBody>
          </Table>
          {props.totalCount > SLC_AUDIT_TRAIL_LIMIT && (
            <Pagination
              className="slc__pagination"
              offset={props.offset}
              pageAction={pageAuditTrail}
              pageShowRange={3}
              scrollTopPageChange
              shouldShowInput
              step={props.limit}
              totalCount={props.totalCount}
            />
          )}
        </ActionableHeaderCard>
      </div>
      {shouldShowDeleteModal && renderDeleteRequestModal()}
    </div>
  );
}

SurveyLibraryChangeRequest.propTypes = {
  getAuditTrail: func,
  setOffset: func,
  auditTrails: array,
  offset: number,
  limit: number,
  totalCount: number,
  pendingSurveyRequests: array,
  deleteSurveyRequest: func,
  isDeletingSurveyRequest: bool,
  hasDeleteSurveyRequestError: bool,
  subscribeSurveys: func,
  initializeAgreement: func,
  showSlideout: func,
  hideSlideout: func,
  updateSurvey: func,
  updatePendingSurvey: func,
  resetDeleteSurveyError: func,
  hasSubGroups: bool
};

export default SurveyLibraryChangeRequest;
